import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { ClaimFlagTag } from '@client/components/claims/ClaimFlagTag';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, openRowOnClick, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Tag } from 'antd';
import { useState } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/claims/claim-flags')({
    staticData: {
        metadata: {
            title: 'Flags',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ClaimFlagsSearch />
        </PageController>
    );
}

type QueryParams = RouterInputs['claimFlag']['listClaimFlags'];

const columnHelper = createColumnHelper<
    NonNullable<RouterOutputs['claimFlag']['listClaimFlags']>['rows'][number],
    NonNullable<QueryParams['orderBy']>
>();

function ClaimFlagsSearch() {
    const orgId = useOrgId();
    const { trpcUtils } = useTrpc();
    const { mutate } = trpc.claimFlag.deleteClaimFlag.useMutation();
    const navigate = useNavigate();

    const [input, setInput] = useState<QueryParams>({});

    const { data, isPending, error, refetch, isFetching } = trpc.claimFlag.listClaimFlags.useQuery(input);

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        queryParams: input,
        onParamsChange: setInput,
        urlKey: 'q',
        queryError: error,
        emptyState: { noRecordsTitle: 'No claim flags yet' },
        columns: [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
                cell({ row }) {
                    return (
                        <ClaimFlagTag
                            data={{
                                name: row.name,
                                type: 'SECONDARY',
                                displayColor: row.displayColor,
                                isSystemDefined: row.isSystemDefined,
                            }}
                        />
                    );
                },
            }),
            columnHelper.accessor('isActive', {
                id: 'isActive',
                header: 'Status',
                sortable: 'isActive',
                width: 140,
                cell({ row }) {
                    return <StatusBadge module="state" status={row.isActive ? 'ACTIVE' : 'DISABLED'} />;
                },
            }),
            columnHelper.accessor('isSystemDefined', {
                id: 'isSystemDefined',
                header: 'Source',
                sortable: 'isSystemDefined',
                width: 140,
                cell({ row }) {
                    return row.isSystemDefined ? (
                        <Tag variant="outlined" color="default">
                            System
                        </Tag>
                    ) : (
                        <Tag variant="outlined" color="purple">
                            Custom
                        </Tag>
                    );
                },
            }),
            columnHelper.actions({
                width: 96,
                cell({ row }) {
                    // Every flag opens its editor (a system flag's toggle and colour stay editable), so Edit is
                    // the keyboard path to what a row click opens; only a custom flag can be deleted.
                    return (
                        <div className="flex items-center justify-end gap-1">
                            <Button.Edit.Link
                                to="/console/$orgId/configuration/claims-products/claims/claim-flags/edit/$claimFlagId"
                                params={{ orgId, claimFlagId: row.id }}
                                aria-label={`Edit ${row.name}`}
                            />
                            {row.isSystemDefined ? null : (
                                <Button.Delete
                                    onConfirm={(onSuccess) =>
                                        mutate(
                                            { id: row.id },
                                            {
                                                onSuccess() {
                                                    onSuccess();
                                                    void trpcUtils.claimFlag.invalidate();
                                                },
                                                onError(error) {
                                                    errorMessage.show(error);
                                                },
                                            },
                                        )
                                    }
                                />
                            )}
                        </div>
                    );
                },
            }),
        ],
    });

    if (error) {
        return <InlineQueryError error={error} onRetry={() => void refetch()} retrying={isFetching} />;
    }

    return (
        <>
            <Outlet />
            <TableToolbar
                actions={
                    <Button.Add.Link
                        to="/console/$orgId/configuration/claims-products/claims/claim-flags/add"
                        params={{
                            orgId,
                        }}
                        variant="primary"
                        label="New flag"
                    />
                }
            />
            <PrismaTable
                {...tableProps}
                onRow={openRowOnClick(({ id }, e) =>
                    navigate(
                        {
                            to: '/console/$orgId/configuration/claims-products/claims/claim-flags/edit/$claimFlagId',
                            params: { orgId, claimFlagId: id },
                        },
                        e,
                    ),
                )}
            />
        </>
    );
}
